// Footing of a site model: the low slab a monument stands on. Pure (no GL), so the rule is testable.
//
//      landmark
//     ┌────────┐ ← top: above every ground sample of the footprint
//   ~~~│ footing│~~~ ground (a hill crown slopes across the footprint)
//   ~~~└────────┘~~~ ← bottom: below every ground sample, so no gap shows on the low side
//
// The mesher lifts a hill's crown toward the hex centre and adds noise, so ground under a round
// footprint differs by up to about 0.4 world units. A slab set at the lowest sample and only 0.1
// thick was buried on the high side and showed as torn white shards.

import type { HexShapes } from '../terrain/heights.ts';
import { crownProfile } from '../terrain/mesher.ts';

export type GroundShapes = Pick<HexShapes, 'base' | 'lift' | 'crown' | 'crownScale' | 'amp'>;

export interface Footing {
  /** Height of the slab's upper face. */
  top: number;
  /** Height of its lower face. */
  bottom: number;
}

const RING_SAMPLES = 8;
const INNER_SHARE = 0.5;

/** Terrain noise is added on top of the crown; its size is `amp` (heights.ts groundNoise, |n| <= 1). */
const NOISE_REACH = 1;

/** The slab's face stands this far above the highest ground and reaches this far below the lowest. */
const LIP = 0.03;
const ROOT = 0.06;

/** Ground height at local offset (lx, lz) of hex t, before the mesher's noise. */
export function crownAt(shapes: GroundShapes, t: number, lx: number, lz: number): number {
  const r = Math.min(1, Math.hypot(lx, lz));
  return shapes.base[t] + shapes.lift[t] + shapes.crown[t] * shapes.crownScale[t] * crownProfile(r);
}

/**
 * The slab under a round footprint of `radius` centred at (cx, cz) inside hex t. Samples the
 * footprint on two rings and the centre, all inside the hex's own rings, and covers the highest
 * and lowest of them plus the terrain noise. Null when any height is not a finite number.
 */
export function footingAt(shapes: GroundShapes, t: number, cx: number, cz: number, radius: number): Footing | null {
  let low = crownAt(shapes, t, cx, cz);
  let high = low;
  for (let i = 0; i < RING_SAMPLES; i++) {
    const a = (i / RING_SAMPLES) * Math.PI * 2;
    for (const share of [INNER_SHARE, 1]) {
      const h = crownAt(shapes, t, cx + Math.cos(a) * radius * share, cz + Math.sin(a) * radius * share);
      low = Math.min(low, h);
      high = Math.max(high, h);
    }
  }

  const noise = shapes.amp[t] * NOISE_REACH;
  const top = high + noise + LIP;
  const bottom = low - noise - ROOT;
  if (!Number.isFinite(top) || !Number.isFinite(bottom)) {
    return null;
  }
  return { top, bottom };
}
